﻿import React from 'react';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Divider, Paper, Tooltip, Typography } from '@mui/material';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import SuccessModal from '~/components/modals/SuccessModal/SuccessModal.tsx';
import DocumentsTable from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ReviewDocuments/DocumentsTable.tsx';
import { RejectCommentsModal } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ReviewDocuments/RejectCommentsModal.tsx';
import { StatusChip } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ApplicationsTable/StatusChip.tsx';
import {
  MANAGER_APPLICATION_REVIEW_DONE_BUTTON_LABEL,
  MANAGER_APPLICATION_REVIEW_REJECT_ALL_BUTTON_LABEL,
  MANAGER_APPLICATION_REVIEW_SUBMIT_BUTTON_LABEL,
  MANAGER_APPLICATION_REVIEW_SUBMITTING_BUTTON_LABEL,
  MANAGER_APPLICATION_REVIEW_SUCCESS_BUTTON_LABEL,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import useManagerApplicationReview from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/hooks/useManagerApplicationReview.ts';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { managerStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/styles.ts';
import { FetchStatus } from '~/store/covenants/types.ts';

const ManagerApplicationReviewPage: React.FC = () => {
  const {
    application,
    applicationId,
    comments,
    createdAtLabel,
    isManager,
    decisions,
    documents,
    documentsPage,
    documentsPageConfiguration,
    documentsRowsPerPage,
    documentsStatus,
    handleCommentSave,
    handleDecisionChange,
    handleMissingCommentsConfirm,
    handleMissingCommentsModalClose,
    handlePreviewDocument,
    handleRejectAllDocuments,
    handleSharedRejectCommentChange,
    handleSubmit,
    isInitialLoading,
    isMissingCommentsModalOpen,
    isReviewEditable,
    isSubmitDisabled,
    isSubmitting,
    getDocumentCommentValue,
    missingApplicationText,
    missingRejectedCommentsCount,
    modalErrorText,
    onCloseErrorModal,
    onCloseSuccessModal,
    onMissingApplicationClose,
    onRowsPerPageChange,
    onSuccessAction,
    setDocumentsPage,
    sharedRejectComment,
    sharedRejectCommentError,
    successText,
    updatingDecisionDocumentIds,
    viewedDocumentIds,
  } = useManagerApplicationReview();

  const applicationNumber = application?.localId ?? applicationId ?? '-';
  const applicationTooltipTitle = `ID заявки: ${application?.id ?? applicationId ?? '-'}`;
  const clientName = application?.contragentFullName || '-';
  if (!isManager) {
    return null;
  }

  if (!applicationId) {
    return <ModalAlert isOpen text={missingApplicationText} onClose={onMissingApplicationClose} />;
  }

  if (isInitialLoading) {
    return (
      <Box sx={commonStyles.centeredLoader}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={managerStyles.reviewPageRoot}>
      <Box sx={managerStyles.reviewPageContent}>
        <Box sx={commonStyles.backRow}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={onSuccessAction}
            sx={commonStyles.backButton}
          >
            Назад
          </Button>
        </Box>

        <Paper elevation={3} sx={managerStyles.reviewPaper}>
          <Box sx={managerStyles.reviewHeaderRow}>
            <Typography variant="h4" align="center" sx={managerStyles.reviewClientName}>
              {clientName}
            </Typography>
          </Box>

          <Box sx={managerStyles.reviewSubtitleBox}>
            <Box sx={managerStyles.reviewMetaStatusRow}>
              <Typography
                variant="body2"
                align="center"
                color="text.primary"
                sx={managerStyles.reviewMetaText}
              >
                <Tooltip title={applicationTooltipTitle} arrow>
                  <Box component="span" sx={managerStyles.reviewApplicationNumber}>
                    {`Заявка №${applicationNumber}`}
                  </Box>
                </Tooltip>
              </Typography>

              {createdAtLabel && (
                <Typography
                  variant="body2"
                  align="center"
                  color="text.primary"
                  sx={managerStyles.reviewMetaText}
                >
                  {createdAtLabel}
                </Typography>
              )}

              {application && <StatusChip status={application.status} />}
            </Box>
          </Box>

          <Divider sx={managerStyles.reviewDivider} />

          <Paper sx={managerStyles.reviewTablePaper} elevation={0} variant="outlined">
            <DocumentsTable
              documents={documents}
              decisions={decisions}
              isReadOnly={!isReviewEditable}
              comments={comments}
              page={documentsPage}
              rowsPerPage={documentsRowsPerPage}
              totalElements={documentsPageConfiguration.totalElements}
              isLoading={documentsStatus === FetchStatus.Loading}
              viewedDocumentIds={viewedDocumentIds}
              updatingDecisionDocumentIds={updatingDecisionDocumentIds}
              onPreviewDocument={handlePreviewDocument}
              onDecisionChange={handleDecisionChange}
              onCommentSave={handleCommentSave}
              getDocumentCommentValue={getDocumentCommentValue}
              onPageChange={setDocumentsPage}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          </Paper>

          <Box sx={managerStyles.reviewFooter}>
            {isReviewEditable && (
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                disabled={isSubmitting || documentsPageConfiguration.totalElements === 0}
                onClick={handleRejectAllDocuments}
                sx={managerStyles.reviewFooterAction}
              >
                {MANAGER_APPLICATION_REVIEW_REJECT_ALL_BUTTON_LABEL}
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              disabled={isSubmitDisabled}
              onClick={
                isReviewEditable
                  ? () => {
                      void handleSubmit();
                    }
                  : onSuccessAction
              }
              sx={managerStyles.reviewSubmit}
            >
              {isReviewEditable
                ? isSubmitting
                  ? MANAGER_APPLICATION_REVIEW_SUBMITTING_BUTTON_LABEL
                  : MANAGER_APPLICATION_REVIEW_SUBMIT_BUTTON_LABEL
                : MANAGER_APPLICATION_REVIEW_DONE_BUTTON_LABEL}
            </Button>
          </Box>
        </Paper>
      </Box>

      <ModalAlert isOpen={!!modalErrorText} text={modalErrorText} onClose={onCloseErrorModal} />
      <RejectCommentsModal
        open={isMissingCommentsModalOpen}
        missingCommentsCount={missingRejectedCommentsCount}
        comment={sharedRejectComment}
        errorText={sharedRejectCommentError}
        isSubmitting={isSubmitting}
        onClose={handleMissingCommentsModalClose}
        onCommentChange={handleSharedRejectCommentChange}
        onConfirm={() => {
          void handleMissingCommentsConfirm();
        }}
      />
      <SuccessModal
        isOpen={!!successText}
        text={successText}
        buttonValue={MANAGER_APPLICATION_REVIEW_SUCCESS_BUTTON_LABEL}
        onClose={onCloseSuccessModal}
        onClick={onSuccessAction}
      />
    </Box>
  );
};

export default ManagerApplicationReviewPage;
